import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

const AdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Task form state
  const [taskForm, setTaskForm] = useState({
    date: new Date().toISOString().split('T')[0],
    stage: "",
    warehouse: "pune",
    sku: "",
    qty: "",
    status: "pending"
  });

  // Batch form state
  const [batchForm, setBatchForm] = useState({
    batch_id: "",
    warehouse: "pune",
    plant_sku: "",
    layer_type: "",
    total_qty: "",
    row_loc: "",
    section_loc: "",
    created_at: new Date().toISOString().split('T')[0]
  });

  const showMessage = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  };

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const url = dateFilter 
        ? `${BASE_URL}/tasks/filter/?warehouse=pune&start_date=${dateFilter}&end_date=${dateFilter}`
        : `${BASE_URL}/tasks/filter/?warehouse=pune`;
      
      const res = await fetch(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      const data = await res.json();
      setTasks(data);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      showMessage("error", "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  // Fetch batches
  const fetchBatches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const url = dateFilter 
        ? `${BASE_URL}/get-active-batches/?warehouse=pune&date=${dateFilter}`
        : `${BASE_URL}/get-active-batches/?warehouse=pune`;
      
      const res = await fetch(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      const data = await res.json();
      setBatches(data.batches || []);
    } catch (err) {
      console.error("Error fetching batches:", err);
      showMessage("error", "Failed to fetch batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "tasks") {
      fetchTasks();
    } else {
      fetchBatches();
    }
  }, [activeTab, dateFilter]);

  // Task selection handlers
  const handleTaskSelect = (taskId) => {
    setSelectedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAllTasks = () => {
    setSelectedTasks(prev =>
      prev.length === tasks.length ? [] : tasks.map(t => t.task_id)
    );
  };

  // Batch selection handlers
  const handleBatchSelect = (batchId) => {
    setSelectedBatches(prev =>
      prev.includes(batchId)
        ? prev.filter(id => id !== batchId)
        : [...prev, batchId]
    );
  };

  const handleSelectAllBatches = () => {
    setSelectedBatches(prev =>
      prev.length === batches.length ? [] : batches.map(b => b.batch_id)
    );
  };

  // Delete selected tasks
// In handleDeleteTasks function:
  const handleDeleteTasks = async () => {
    if (!selectedTasks.length) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedTasks.length} task(s)?`)) {
        return;
    }

    try {
        const token = localStorage.getItem("token");
        
        // Use bulk delete endpoint
        const res = await fetch(`${BASE_URL}/tasks/bulk_delete_task/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ task_ids: selectedTasks }),
        });

        const data = await res.json();
        
        if (!res.ok) {
        throw new Error(data.error || "Failed to delete tasks");
        }

        showMessage("success", data.message);
        setSelectedTasks([]);
        fetchTasks();
    } catch (err) {
        console.error("Error deleting tasks:", err);
        showMessage("error", err.message || "Failed to delete tasks");
    }
    };

  // Delete selected batches
  const handleDeleteBatches = async () => {
    if (!selectedBatches.length) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedBatches.length} batch(es)?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      for (const batchId of selectedBatches) {
        const res = await fetch(`${BASE_URL}/batch/delete/${batchId}/`, {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        
        if (!res.ok) {
          throw new Error(`Failed to delete batch ${batchId}`);
        }
      }

      showMessage("success", `Successfully deleted ${selectedBatches.length} batch(es)`);
      setSelectedBatches([]);
      fetchBatches();
    } catch (err) {
      console.error("Error deleting batches:", err);
      showMessage("error", "Failed to delete batches");
    }
  };

  // Create task
// In handleCreateTask function:
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/tasks/create_task/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
        },
        body: JSON.stringify({
            date: taskForm.date,
            stage: taskForm.stage,
            warehouse: taskForm.warehouse,
            sku: taskForm.sku,
            qty: taskForm.qty,
            status: taskForm.status
        }),
        });

        const data = await res.json();
        
        if (!res.ok) {
        throw new Error(data.error || "Failed to create task");
        }

        showMessage("success", "Task created successfully");
        setShowCreateTask(false);
        setTaskForm({
        date: new Date().toISOString().split('T')[0],
        stage: "",
        warehouse: "pune",
        sku: "",
        qty: "",
        status: "pending"
        });
        fetchTasks();
    } catch (err) {
        console.error("Error creating task:", err);
        showMessage("error", err.message || "Failed to create task");
    }
    };

  // Create batch
// In handleCreateBatch function, modify the API call:
  const handleCreateBatch = async (e) => {
    e.preventDefault();
    try {
        const token = localStorage.getItem("token");
        
        // Create FormData to handle both JSON and potential file uploads
        const formData = new FormData();
        formData.append("warehouse", batchForm.warehouse);
        formData.append("plant_sku", batchForm.plant_sku);
        formData.append("layer_type", batchForm.layer_type);
        formData.append("total_qty", batchForm.total_qty);
        formData.append("row_loc", batchForm.row_loc);
        formData.append("section_loc", batchForm.section_loc);
        formData.append("created_at", batchForm.created_at); // Add the manual date

        const res = await fetch(`${BASE_URL}/create-batch/`, {
        method: "POST",
        headers: {
            Authorization: `Token ${token}`,
            // Remove Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
        });

        const data = await res.json();
        
        if (!res.ok) {
        throw new Error(data.error || "Failed to create batch");
        }

        showMessage("success", "Batch created successfully");
        setShowCreateBatch(false);
        setBatchForm({
        batch_id: "",
        warehouse: "pune",
        plant_sku: "",
        layer_type: "",
        total_qty: "",
        row_loc: "",
        section_loc: "",
        created_at: new Date().toISOString().split('T')[0]
        });
        fetchBatches();
    } catch (err) {
        console.error("Error creating batch:", err);
        showMessage("error", err.message || "Failed to create batch");
    }
    };

  return (
    <>
      <Navbar />
      <div className="p-4 max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {feedback && (
          <div className={`rounded-lg border px-3 py-2 text-sm mb-4 ${
            feedback.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-green-50 border-green-200 text-green-700"
          }`}>
            {feedback.message}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
              <p className="text-xs text-gray-600 mt-1">Manage tasks and batches</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm font-semibold"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Tabs */}
<div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
  <div className="flex border-b border-gray-200">

    {/* Tasks Tab */}
    <button
      onClick={() => setActiveTab("tasks")}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition rounded-t-md
        ${
          activeTab === "tasks"
            ? "bg-blue-600 text-white border-blue-700"
            : "bg-gray-400 text-white border-transparent hover:bg-gray-500"
        }
      `}
    >
      Tasks ({tasks.length})
    </button>

    {/* Batches Tab */}
    <button
      onClick={() => setActiveTab("batches")}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition rounded-t-md
        ${
          activeTab === "batches"
            ? "bg-blue-600 text-white border-blue-700"
            : "bg-gray-400 text-white border-transparent hover:bg-gray-500"
        }
      `}
    >
      Batches ({batches.length})
    </button>

  </div>
</div>



        {/* Filters and Actions */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Filter by Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={() => setDateFilter("")}
                className="px-4 py-2 border border-gray-300 rounded text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Clear Filter
              </button>
              {activeTab === "tasks" ? (
                <>
                  <button
                    onClick={() => setShowCreateTask(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
                  >
                    Create Task
                  </button>
                  {selectedTasks.length > 0 && (
                    <button
                      onClick={handleDeleteTasks}
                      className="px-4 py-2 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700"
                    >
                      Delete Selected ({selectedTasks.length})
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowCreateBatch(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
                  >
                    Create Batch
                  </button>
                  {selectedBatches.length > 0 && (
                    <button
                      onClick={handleDeleteBatches}
                      className="px-4 py-2 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700"
                    >
                      Delete Selected ({selectedBatches.length})
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center h-40 bg-white rounded-lg border border-gray-200">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading...</p>
            </div>
          </div>
        ) : activeTab === "tasks" ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Tasks Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        onChange={handleSelectAllTasks}
                        checked={selectedTasks.length === tasks.length && tasks.length > 0}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Task ID</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Stage</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">SKU</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Quantity</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr key={task.task_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.task_id)}
                          onChange={() => handleTaskSelect(task.task_id)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{task.task_id}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{task.date}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">Layer {task.stage}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{task.sku_name || task.sku}</td>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900">{task.qty}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          task.status === 'pending' 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {task.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tasks.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-500">
                No tasks found
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Batches Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        onChange={handleSelectAllBatches}
                        checked={selectedBatches.length === batches.length && batches.length > 0}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Batch ID</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Created</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Layer</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">SKU</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Total Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Available</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {batches.map((batch) => (
                    <tr key={batch.batch_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedBatches.includes(batch.batch_id)}
                          onChange={() => handleBatchSelect(batch.batch_id)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{batch.batch_id}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{batch.created_at}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">Layer {batch.layer_type}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{batch.plant_sku_code}</td>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900">{batch.total_qty}</td>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900">{batch.avl_qty}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {batch.row_loc && batch.section_loc ? `${batch.row_loc}-${batch.section_loc}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {batches.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-500">
                No batches found
              </div>
            )}
          </div>
        )}

        {/* Create Task Modal */}
        {showCreateTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Task</h3>
              <form onSubmit={handleCreateTask} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={taskForm.date}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Stage</label>
                  <select
                    value={taskForm.stage}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, stage: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                  >
                    <option value="">Select Stage</option>
                    <option value="1">Layer 1</option>
                    <option value="2">Layer 2</option>
                    <option value="3">Layer 3</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SKU</label>
                  <input
                    type="text"
                    value={taskForm.sku}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, sku: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    placeholder="Enter SKU"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    value={taskForm.qty}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, qty: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    placeholder="Enter quantity"
                    required
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateTask(false)}
                    className="flex-1 py-2 border border-gray-300 rounded text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
                  >
                    Create Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create Batch Modal */}
        {showCreateBatch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Batch</h3>
              <form onSubmit={handleCreateBatch} className="space-y-3">

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Created Date</label>
                  <input
                    type="date"
                    value={batchForm.created_at}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, created_at: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Layer Type</label>
                  <select
                    value={batchForm.layer_type}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, layer_type: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                  >
                    <option value="">Select Layer</option>
                    <option value="1">Layer 1</option>
                    <option value="2">Layer 2</option>
                    <option value="3">Layer 3</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Plant SKU</label>
                  <input
                    type="text"
                    value={batchForm.plant_sku}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, plant_sku: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    placeholder="Enter plant SKU"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Total Quantity</label>
                  <input
                    type="number"
                    value={batchForm.total_qty}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, total_qty: e.target.value }))}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    placeholder="Enter total quantity"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Row Location</label>
                    <input
                      type="text"
                      value={batchForm.row_loc}
                      onChange={(e) => setBatchForm(prev => ({ ...prev, row_loc: e.target.value }))}
                      className="w-full border border-gray-300 p-2 rounded text-sm"
                      placeholder="Row"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Section Location</label>
                    <input
                      type="text"
                      value={batchForm.section_loc}
                      onChange={(e) => setBatchForm(prev => ({ ...prev, section_loc: e.target.value }))}
                      className="w-full border border-gray-300 p-2 rounded text-sm"
                      placeholder="Section"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateBatch(false)}
                    className="flex-1 py-2 border border-gray-300 rounded text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
                  >
                    Create Batch
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminPage;